// Omnix/client/ui/ContextualSidebar.js
import { ComponentBase } from "../core/ComponentBase.js";
import { TagInput } from "../components/TagInput.js";

export class ContextualSidebar extends ComponentBase {
    static styles = `
        .contextual-sidebar {
            width: 320px;
            min-width: 320px;
            border-left: 1px solid var(--bs-border-color);
            /* --- FIX: Use the correct background color for the pane --- */
            background-color: var(--bs-secondary-bg);
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
        }
        .sidebar-content {
            flex-grow: 1;
            overflow-y: auto;
            padding: 1rem;
        }
        .sidebar-placeholder {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            height: 100%;
            color: var(--bs-secondary-color);
            text-align: center;
        }
        .sidebar-placeholder i { font-size: 3rem; margin-bottom: 1rem; }
        .sidebar-section { margin-bottom: 1.5rem; }
        .sidebar-section-title {
            font-weight: 600;
            font-size: 0.9rem;
            color: var(--bs-secondary-color);
            text-transform: uppercase;
            margin-bottom: 0.75rem;
        }

                .sidebar-tabs {
            flex-shrink: 0;
            border-bottom: 1px solid var(--bs-border-color);
        }
        .sidebar-tab-content {
            flex-grow: 1;
            overflow-y: auto;
        }
        .sidebar-pane {
            padding: 1rem;
            display: none; /* Hide panes by default */
        }
        .sidebar-pane.active {
            display: block; /* Show active pane */
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
            .contextual-sidebar {
                position: absolute;
                top: 0;
                right: 0;
                width: 100%;
                height: 100%;
                z-index: 1050;
                transform: translateX(100%);
                transition: transform 0.3s ease-in-out;
                border-left: none;
                background-color: var(--bs-body-bg);
            }
            .contextual-sidebar.visible {
                transform: translateX(0);
            }
        }
    `;

    constructor(config) {
        super(config);
        this.conversation = null;
        this.assignableAgents = [];
        this.tagInput = null;
        this.notesPanel = null;
        this.activeTab = 'properties'; // Default tab

        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'contextual-sidebar';
        this.notesPanel = await this.createManagedComponent('NotesPanel', null, {
            instanceId: `${this.config.instanceId}-notes`
        });

        this.tagInput = await this.createManagedComponent('TagInput', null, {
            instanceId: `${this.config.instanceId}-tags`,
            placeholder: 'Add a tag...',
            options: [
                { value: 'urgent', label: 'Urgent' },
                { value: 'sales-lead', label: 'Sales Lead' },
                { value: 'support-ticket', label: 'Support Ticket' },
                { value: 'vip', label: 'VIP' },
            ]
        });
        // Attach the event listener once.
        this.tagInput.on('change', (tags) => this.handleTagsChange(tags));

        this.contactOverviewPanel = await this.createManagedComponent('ContactOverviewPanel', null, {
            instanceId: `${this.config.instanceId}-contact-overview`
        });


        this.ticketPanel = await this.createManagedComponent('TicketPanel', null, {
            instanceId: `${this.config.instanceId}-ticket`
        });
        this.ticketPanel.on('ticketAction', (e) => this.handleTicketAction(e));


        await super.initialize();
    }

    async initAfterLogin() {
        await this.fetchAssignableAgents();
    }

    async fetchAssignableAgents() {
        try {
            this.assignableAgents = await this.appClient.rpcClient.call('UnifiedInboxRpc.getAssignableAgents', {});
        } catch (error) {
            this.log.error("Failed to fetch assignable agents for sidebar:", error);
        }
    }

    setConversation(conversation) {
        this.conversation = conversation;
        if (this.notesPanel) {
            this.notesPanel.setConversation(conversation);
        }

        if (this.contactOverviewPanel) {
            this.contactOverviewPanel.setContact(conversation ? conversation.contactId : null);
        }

        if (this.ticketPanel) {
            this.ticketPanel.setConversation(conversation);
        }

        if (this.container) this.render();
    }

    render() {
        this.container.innerHTML = '';
        if (!this.conversation) {
            this.container.innerHTML = `
                <div class="sidebar-placeholder">
                    <i class="bi bi-info-circle"></i>
                    <p>${this.t('Select a conversation to see its details and actions.')}</p>
                </div>
            `;
        } else {
            this.renderHeader();
            this.renderTabs();
            const content = document.createElement('div');
            content.className = 'sidebar-content';
            this.renderPropertiesPane(content);
            this.renderNotesPane(content);
            this.renderTicketPane(content);
            this.container.appendChild(content);
        }
        return this.container;
    }

    renderHeader() {
        const header = document.createElement('div');
        header.className = 'd-flex justify-content-end p-2 border-bottom d-md-none'; // Only show on mobile
        header.innerHTML = `<button class="btn-close" data-action="close-sidebar"></button>`;
        header.querySelector('[data-action="close-sidebar"]').addEventListener('click', () => this.emit('close'));
        this.container.appendChild(header);
    }

    renderTabs() {
        const tabs = document.createElement('ul');
        tabs.className = 'nav nav-tabs nav-fill sidebar-tabs';
        tabs.innerHTML = `
            <li class="nav-item">
                <a class="nav-link ${this.activeTab === 'properties' ? 'active' : ''}" href="#" data-tab="properties">${this.t('Details')}</a>
            </li>
            <li class="nav-item">
                <a class="nav-link ${this.activeTab === 'notes' ? 'active' : ''}" href="#" data-tab="notes">${this.t('Notes')}</a>
            </li>
            <li class="nav-item">
                <a class="nav-link ${this.activeTab === 'ticket' ? 'active' : ''}" href="#" data-tab="ticket">${this.t('Ticket')}</a>
            </li>
        `;
        tabs.addEventListener('click', (e) => {
            e.preventDefault();
            const tabId = e.target.dataset.tab;
            if (tabId) {
                this.activeTab = tabId;
                this.render(); // Re-render to show the correct active tab
            }
        });
        this.container.appendChild(tabs);
    }
    renderPropertiesPane(parentElement) {
        const section = document.createElement('div');
        section.className = `sidebar-pane ${this.activeTab === 'properties' ? 'active' : ''}`;

        const overviewContainer = document.createElement('div');
        overviewContainer.className = 'sidebar-section';
        overviewContainer.appendChild(this.contactOverviewPanel.render());
        section.appendChild(overviewContainer);

        const divider = document.createElement('hr');
        divider.className = 'my-3';
        section.appendChild(divider);

        // --- Status Dropdown ---
        const statusOptions = ['open', 'pending', 'closed'].map(s =>
            `<option value="${s}" ${this.conversation.status === s ? 'selected' : ''}>${this.t(s.charAt(0).toUpperCase() + s.slice(1))}</option>`
        ).join('');

        const statusSection = document.createElement('div');
        statusSection.className = 'mb-3';
        statusSection.innerHTML = `
        <label class="form-label small">${this.t('Status')}</label>
        <select class="form-select form-select-sm" id="conv-status-select">${statusOptions}</select>
    `;
        statusSection.querySelector('#conv-status-select').addEventListener('change', (e) => {
            this.emit('statusChanged', { status: e.target.value });
        });
        section.appendChild(statusSection);

        // --- Assignee Dropdown ---
        const assigneeOptions = this.assignableAgents.map(agent =>
            `<option value="${agent.value}" ${this.conversation.assigneeId === agent.value ? 'selected' : ''}>${this.t(agent.label)}</option>`
        ).join('');

        const assigneeSection = document.createElement('div');
        assigneeSection.className = 'mb-3';
        assigneeSection.innerHTML = `
        <label class="form-label small">${this.t('Assignee')}</label>
        <select class="form-select form-select-sm" id="conv-assignee-select">
            <option value="">-- ${this.t('Unassigned')} --</option>
            ${assigneeOptions}
        </select>
    `;
        assigneeSection.querySelector('#conv-assignee-select').addEventListener('change', (e) => {
            this.emit('assigneeChanged', { assigneeId: e.target.value || null });
        });
        section.appendChild(assigneeSection);

        // --- Tags Section (Corrected Logic) ---
        const tagsSection = document.createElement('div');
        tagsSection.className = 'mb-3';
        tagsSection.innerHTML = `<label class="form-label small">${this.t('Tags')}</label>`;

        // The this.tagInput instance was already created in initialize().
        // We now update its value and get its rendered element.
        this.tagInput.setValue(this.conversation.tags || []);
        const tagInputElement = this.tagInput.render();

        tagsSection.appendChild(tagInputElement);
        section.appendChild(tagsSection);
        // --- End Tags Section ---

        parentElement.appendChild(section);
    }


    renderNotesPane(parentElement) {
        const pane = document.createElement('div');
        pane.className = `sidebar-pane ${this.activeTab === 'notes' ? 'active' : ''}`;
        pane.appendChild(this.notesPanel.render());
        parentElement.appendChild(pane);
    }

    renderTicketPane(parentElement) {
        const pane = document.createElement('div');
        pane.className = `sidebar-pane ${this.activeTab === 'ticket' ? 'active' : ''}`;
        pane.appendChild(this.ticketPanel.render()); // Append the rendered content of the TicketPanel
        parentElement.appendChild(pane);
    }


    async handleTagsChange(newTags) {
        if (!this.conversation) return;
        try {
            await this.appClient.rpcClient.call('UnifiedInboxRpc.updateConversationDetails', {
                conversationId: this.conversation._id,
                updates: { tags: newTags }
            });
            this.appClient.notifier.success("Tags updated.");
        } catch (error) {
            this.log.error("Failed to update tags:", error);
            this.appClient.notifier.error("Could not update tags.");
        }
    }

    handleTicketAction({ action }) {
        const unifiedInbox = this.appClient.layoutEngine.componentManager.componentInstances['mainInbox'];
        if (!unifiedInbox || !unifiedInbox.ticketForm) {
            this.log.error("Could not find UnifiedInbox or its TicketForm to create a ticket.");
            return;
        }

        if (action === 'create') {
            const initialData = {
                contactId: this.conversation.contactId,
                conversationId: this.conversation._id,
                subject: `Ticket from conversation: ${this.conversation.lastInteractionSnippet || ''}`.substring(0, 100),
                status: 'new',
                priority: 'normal'
            };
            unifiedInbox.ticketForm.show('create', initialData, this.assignableAgents);
        } else if (action === 'link') {
            this.appClient.notifier.info("Linking to existing tickets will be implemented in a future turn.");
        }
    }


}